import { useCallback, useEffect, useRef, useState } from 'react';
import type { DisplayRecoveryExchange } from '@pokerroom/types';

export function useDisplayPairingRecovery(
  apiBase: string,
  onPaired: (credential: string) => Promise<void>,
) {
  const secret = useRef<{ proof: string; deadline: number } | null>(null);
  const generation = useRef(0);
  const [identifier, setIdentifier] = useState('');
  const [state, setState] = useState<'idle' | 'pending' | 'paused' | 'expired' | 'unavailable'>(
    'idle',
  );
  const [retry, setRetry] = useState(0);
  const onPairedRef = useRef(onPaired);
  onPairedRef.current = onPaired;

  const abandon = useCallback(() => {
    generation.current++;
    secret.current = null;
    setIdentifier('');
    setState('idle');
  }, []);

  const start = async () => {
    abandon();
    const epoch = generation.current;
    try {
      if (!globalThis.crypto?.subtle || !globalThis.crypto.getRandomValues)
        throw new Error('unavailable');
      const bytes = globalThis.crypto.getRandomValues(new Uint8Array(32));
      const digest = new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', bytes));
      if (generation.current !== epoch) return;
      const hex = (values: Uint8Array) =>
        Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
      secret.current = { proof: hex(bytes), deadline: Date.now() + 300000 };
      bytes.fill(0);
      setIdentifier(hex(digest.slice(0, 16)).match(/.{4}/g)!.join('-'));
      setState('pending');
    } catch {
      if (generation.current === epoch) setState('unavailable');
    }
  };

  useEffect(() => {
    return () => {
      generation.current++;
      secret.current = null;
    };
  }, []);

  useEffect(() => {
    if (state !== 'pending' || !secret.current) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    const epoch = generation.current;
    const active = () => !controller.signal.aborted && generation.current === epoch;
    const expire = () => {
      if (!active()) return;
      secret.current = null;
      setState('expired');
      controller.abort();
    };
    expiryTimer = setTimeout(expire, Math.max(0, secret.current.deadline - Date.now()));
    const poll = async () => {
      const current = secret.current;
      if (!active() || !current) return;
      if (Date.now() >= current.deadline) {
        expire();
        return;
      }
      const request = new AbortController();
      const abort = () => request.abort();
      controller.signal.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, 5000);
      try {
        const response = await fetch(apiBase + '/api/display/pairing/recovery-exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ proof: current.proof }),
          signal: request.signal,
        });
        if (!active()) return;
        if (!response.ok) {
          setState(response.status === 429 || response.status === 503 ? 'paused' : 'expired');
          if (response.status !== 429 && response.status !== 503) secret.current = null;
          return;
        }
        const body = await response.json();
        const result: DisplayRecoveryExchange = body.data ?? body;
        if (!active()) return;
        if ('credential' in result && typeof result.credential === 'string') {
          secret.current = null;
          await onPairedRef.current(result.credential);
          if (active()) abandon();
          return;
        }
        if (!('pending' in result) || result.pending !== true) {
          setState('paused');
          return;
        }
        timer = setTimeout(() => void poll(), 2000);
      } catch {
        if (active()) setState('paused');
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener('abort', abort);
      }
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => {
      controller.abort();
      clearTimeout(timer);
      clearTimeout(expiryTimer);
    };
  }, [apiBase, state, retry, abandon]);

  // Paused screens still expire; a retry never starts a fresh five-minute lifetime.
  useEffect(() => {
    if (state !== 'paused' || !secret.current) return;
    const timer = setTimeout(
      () => {
        secret.current = null;
        setState('expired');
      },
      Math.max(0, secret.current.deadline - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [state]);

  return {
    identifier,
    state,
    start,
    abandon,
    resume: () => {
      if (!secret.current || Date.now() >= secret.current.deadline) {
        secret.current = null;
        setState('expired');
        return;
      }
      setRetry((value) => value + 1);
      setState('pending');
    },
  };
}
